import { defineNuxtPlugin, useRuntimeConfig } from '#app'

/**
 * @class TikTok
 */
class TikTok {
  constructor(options) {
    this.eventsQueue = []
    this.ttqReady = false // 改用 ready 狀態標記
    this.options = options
    this.isEnabled = !options.disabled
  }

  /**
   * 標記 SDK 已連動，並開始發送暫存隊列
   */
  setTtqReady() {
    this.ttqReady = true
    this.send()
  }

  /**
   * 內部扁平化工具：將深層物件轉為一層，並過濾掉空值
   */
  flatten(data, prefix = '') {
    const result = {}
    for (const key in data) {
      if (Object.prototype.hasOwnProperty.call(data, key)) {
        const value = data[key]
        const newKey = prefix ? `${prefix}_${key}` : key

        if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
          Object.assign(result, this.flatten(value, newKey))
        } else if (value !== '' && value !== null && value !== undefined) {
          result[newKey] = value
        }
      }
    }
    return result
  }

  init() {
    if (!this.options.pixelId) {
      if (this.options.debug) this.log('Skipping init: pixelId is not set')
      return
    }
    this.query('load', this.options.pixelId)
    this.query('page')
  }

  track(event = null, parameters = null) {
    if (!event) {
      event = this.options.track
    }
    this.query('track', event, parameters)
  }

  /**
   * 將指令推入隊列
   */
  query(cmd, eventOrPixelId, parameters = null) {
    if (!this.isEnabled) return

    // 處理參數扁平化
    let processedParameters = parameters
    if (cmd === 'track' && parameters && typeof parameters === 'object') {
      processedParameters = this.flatten(this.processParameters(eventOrPixelId, parameters))
    }

    this.eventsQueue.push({
      cmd,
      eventOrPixelId,
      parameters: processedParameters,
    })

    this.send()
  }

  /**
   * 針對 TikTok 官方事件的參數補強
   */
  processParameters(eventName, parameters) {
    if (!parameters) return null
    const processed = { ...parameters }

    // 特定事件傳入特定參數
    if (eventName === 'CompletePayment') {
      return {
        content_id: 'Digital',
        content_type: 'product',
        event_id: processed.transaction_id || '',
        value: processed.value || processed.app_value || 0,
        currency: processed.currency || '',
        ...processed,
      }
    }
    // 特定事件傳入特定參數
    if (eventName === 'CompleteRegistration') {
      return {
        content_type: 'registration',
        content_name: 'registration',
        event_id: processed.uidSite || '',
        ...processed,
      }
    }
    // 特定事件傳入特定參數
    if (eventName === 'InitiateCheckout') {
      return {
        content_id: 'Digital',
        content_type: 'product',
        ...processed,
      }
    }
    // 特定事件傳入特定參數
    if (eventName === 'ViewContent') {
      return {
        content_id: 'Digital',
        ...processed,
      }
    }
    return processed
  }

  /**
   * 核心發送邏輯：直接操作 window.ttq
   */
  send() {
    // 確保在瀏覽器端且 window.ttq 已存在
    if (typeof window === 'undefined' || !window.ttq) return

    while (this.eventsQueue.length) {
      const event = this.eventsQueue.shift()

      if (this.options.debug) {
        this.log('Send event:', event.eventOrPixelId || event.cmd, event.parameters)
      }

      try {
        // 直接使用全域 window.ttq，它是陣列或函式都能正確接收指令
        if (event.parameters) {
          window.ttq.track(event.eventOrPixelId, event.parameters)
        } else if (event.cmd === 'track') {
          window.ttq.track(event.eventOrPixelId)
        } else {
          // 處理 load 或 page 指令
          window.ttq[event.cmd](event.eventOrPixelId)
        }
      } catch (e) {
        console.error('[TikTok Pixel] Error executing window.ttq:', e)
      }
    }
  }

  log(...messages) {
    // console.info('%c[TikTok Pixel]', 'background: #000; color: #ff0050; padding: 2px 4px; border-radius: 3px;', ...messages)
  }
}

export default defineNuxtPlugin((nuxtApp) => {
  const config = useRuntimeConfig()

  const parsedOptions = {
    pixelId: config.public.tiktokPixelId || null,
    track: 'PageView',
    disabled: false, // 如果沒有 Pixel ID 則自動禁用，避免無效的事件堆積
    debug: config.public.tiktokPixelDebug || true, // 開發階段強行開啟
  }

  const instance = new TikTok(parsedOptions)

  if (typeof window !== 'undefined') {
    // 檢查 SDK 是否已載入（例如 GTM 載入過）
    if (window.ttq) {
      instance.setTtqReady()
      if (parsedOptions.pixelId) instance.init()
    } else {
      // 標準載入腳本
      !(function (w, d, t) {
        w.TiktokAnalyticsObject = t
        var ttq = (w[t] = w[t] || [])
        ttq.methods = ['track', 'load', 'identify', 'getInstance']
        ttq.setAndDefer = function (t, e) {
          t[e] = function () {
            t.push([e].concat(Array.prototype.slice.call(arguments, 0)))
          }
        }
        for (var i = 0; i < ttq.methods.length; i++) {
          ttq.setAndDefer(ttq, ttq.methods[i])
        }
        ttq.instance = function (t) {
          return ttq._i[t] || ttq
        }
        ttq.load = function (e, n) {
          var o = 'https://analytics.tiktok.com/i18n/pixel/events.js'
          ttq._i = ttq._i || {}
          ttq._i[e] = []
          ttq._i[e]._u = o
          ttq._t = ttq._t || {}
          ttq._t[e] = +new Date()
          ttq._o = ttq._o || {}
          ttq._o[e] = n || {}
          var c = d.createElement('script')
          c.type = 'text/javascript'
          c.async = !0
          c.src = o + '?sdkid=' + e + '&lib=' + t
          var a = d.getElementsByTagName('script')[0]
          a.parentNode.insertBefore(c, a)
        }

        // 初始化 instance
        instance.setTtqReady()
        if (parsedOptions.pixelId) {
          instance.init()
        }
      })(window, document, 'ttq')
    }
  }

  return {
    provide: {
      tiktok: instance,
    },
  }
})
